import IconButton from '@/global/components/Button/IconButton'
import { optionsColumn } from '@/global/components/DataGrid/hooks/useDataGridPro'
import { GridColDefPro } from '@/global/components/DataGrid/utils/types'
import ContentGuide from '@/global/guides/ContentGuide'
import { cn } from '@/lib/utils'
import { PreviewEspelho, compactarJornada } from '@acme/core'
import { Checkbox, TextField } from '@mui/material'
import IconeAjuda from 'global/components/IconeAjuda'
import { PlusIcon, Trash2Icon } from 'lucide-react'
import { useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import { TabelaHorarios } from 'service/api'

export default function PreviewEspelhoForm() {
    const { watch, setValue } = useFormContext()

    const horarios = watch('horarios')
    const totalSemanal = watch('totSem')
    const previewEspelho: PreviewEspelho = watch('previewEspelho')
    const lastUpdate = watch('updatedAt')

    const memo = useMemo(() => {
        const rows = previewEspelho?.horarios ?? compactarJornada(horarios)
        const columns = mapColumnsEntradasSaidas(previewEspelho, horarios)

        return {
            rows: rows.map(({ entradasSaidas, ...rest }, id) => ({
                ...entradasSaidas,
                ...rest,
                id,
            })),
            columns: [
                {
                    ...optionsColumn,
                    editable: true,
                    field: 'dia',
                    headerName: 'DIA',
                    flex: 1,
                    minWidth: 100,
                },
                ...columns,
                {
                    ...optionsColumn,
                    editable: true,
                    field: 'total',
                    headerName: 'TOT',
                    width: 100,
                },
                ...(!previewEspelho
                    ? []
                    : [
                          {
                              ...optionsColumn,
                              field: '_excluir',
                              width: 50,
                              renderCell: ({ row: { id } }: any) => (
                                  <IconButton
                                      title="Excluir linha"
                                      sx={{ mx: 'auto' }}
                                      size="small"
                                      onClick={() => {
                                          setValue('previewEspelho', {
                                              ...previewEspelho,
                                              horarios:
                                                  previewEspelho.horarios.filter(
                                                      (_, i) => i !== id,
                                                  ),
                                          })
                                          setValue('updatedAt', new Date())
                                      }}
                                  >
                                      <Trash2Icon className="text-red-500" />
                                  </IconButton>
                              ),
                          },
                      ]),
            ] as GridColDefPro[],
        }
    }, [lastUpdate])

    return (
        <ContentGuide.Card className="size-full gap-4">
            <div className="w-full justify-center flex items-center">
                <h6 className="text-center text-lg">
                    Previsualização do espelho de ponto
                </h6>
                <IconeAjuda>
                    É assim que a tabela de horários será exibida no pdf do
                    espelho de ponto.
                    <br />
                    Desative gerar automaticamente e clique sobre os campos para
                    editar.
                </IconeAjuda>
                <div style={{ flex: 1 }} />
                <div className="inline-flex items-center">
                    <Checkbox
                        checked={!previewEspelho}
                        onChange={() => {
                            if (!previewEspelho)
                                setValue('previewEspelho', {
                                    totalSemanal,
                                    horarios: compactarJornada(horarios),
                                })
                            else setValue('previewEspelho', undefined)
                            setValue('updatedAt', new Date())
                        }}
                    />
                    <span className="select-none">Gerar automaticamente</span>
                </div>
            </div>
            <div
                className={cn('w-full max-h-[unset]', {
                    'opacity-50 pointer-events-none select-none':
                        !previewEspelho,
                })}
            >
                <div
                    className={cn(
                        'flex flex-row justify-between w-full h-fit',
                        'p-2 border border-accent border-b-0 rounded-t-sm mb-0',
                    )}
                >
                    <div className="inline-flex items-center">
                        <span className="select-none">
                            Jornada de trabalho contratual
                        </span>
                        <IconButton
                            className="ml-2 shadow-sm ring-1 ring-accent p-1"
                            size="small"
                            title="Adicionar linha"
                            onClick={() => {
                                setValue('previewEspelho', {
                                    ...previewEspelho,
                                    horarios: [
                                        ...previewEspelho!.horarios,
                                        {
                                            dia: 'Novo dia',
                                            entradasSaidas: {
                                                Ent1: '',
                                                Sai1: '',
                                            },
                                            total: '00:00',
                                        },
                                    ],
                                })
                                setValue('updatedAt', new Date())
                            }}
                        >
                            <PlusIcon className="text-green-600" />
                        </IconButton>
                    </div>
                    {!previewEspelho ? (
                        <span>{totalSemanal}h semanais</span>
                    ) : (
                        <div className="inline-flex items-center">
                            <TextField
                                sx={{
                                    '& .MuiInputBase-root': {
                                        width: '95px',
                                        height: '25px',
                                    },
                                }}
                                value={previewEspelho?.totalSemanal}
                                variant="outlined"
                                size="small"
                                InputProps={{ endAdornment: 'h' }}
                                inputProps={{
                                    options: {
                                        blocks: [2, 2],
                                        delimiters: [':'],
                                        numericOnly: true,
                                    },
                                }}
                                onChange={({ target: { value } }) => {
                                    setValue('previewEspelho', {
                                        ...previewEspelho,
                                        totalSemanal: value,
                                    })
                                    setValue('updatedAt', new Date())
                                }}
                            />
                            <span style={{ userSelect: 'none' }}>
                                &nbsp;semanais
                            </span>
                        </div>
                    )}
                </div>
                <ContentGuide.DataGrid
                    key={String(memo.columns.length)}
                    loading={false}
                    ToolbarProps={{ hideToolbar: true }}
                    style={{
                        marginTop: 0,
                    }}
                    columns={memo.columns}
                    rows={memo.rows}
                    hideFooter
                    density="compact"
                    onCellEditCommit={
                        !previewEspelho
                            ? undefined
                            : ({ id, field, value }) => {
                                  const isHorarioValido = (
                                      value: string = '',
                                  ) => /^\d{2,3}:\d{2}$/.test(value) || !value
                                  if (field === 'dia') {
                                      setValue('previewEspelho', {
                                          ...previewEspelho,
                                          horarios: previewEspelho.horarios.map(
                                              (h, i) =>
                                                  i === id
                                                      ? {
                                                            ...h,
                                                            [field]: value,
                                                        }
                                                      : h,
                                          ),
                                      })
                                  } else if (
                                      isHorarioValido(value) &&
                                      field !== 'total'
                                  ) {
                                      setValue('previewEspelho', {
                                          ...previewEspelho,
                                          horarios: previewEspelho.horarios.map(
                                              (h, i) =>
                                                  i === id
                                                      ? {
                                                            ...h,
                                                            entradasSaidas: {
                                                                ...h.entradasSaidas,
                                                                [field]: value,
                                                            },
                                                        }
                                                      : h,
                                          ),
                                      })
                                  } else if (
                                      isHorarioValido(value) &&
                                      field === 'total'
                                  ) {
                                      setValue('previewEspelho', {
                                          ...previewEspelho,
                                          horarios: previewEspelho.horarios.map(
                                              (h, i) =>
                                                  i === id
                                                      ? {
                                                            ...h,
                                                            [field]: value,
                                                        }
                                                      : h,
                                          ),
                                      })
                                  }
                                  setValue('updatedAt', new Date())
                              }
                    }
                />
            </div>
        </ContentGuide.Card>
    )
}

function mapColumnsEntradasSaidas(
    previewEspelho: any,
    horarios: TabelaHorarios['horarios'],
) {
    const columns = []
    for (let i = 1; i <= 3; i++) {
        if (previewEspelho !== undefined) {
            columns.push({
                ...optionsColumn,
                editable: true,
                field: `Ent${i}`,
                headerName: 'ENT',
                flex: 1,
            })
            columns.push({
                ...optionsColumn,
                editable: true,
                field: `Sai${i}`,
                headerName: 'SAI',
                flex: 1,
            })
        } else {
            const entExists = horarios.find((h: any) => h[`Ent${i}`])
            const saiExists = horarios.find((h: any) => h[`Sai${i}`])

            if (entExists)
                columns.push({
                    ...optionsColumn,
                    editable: true,
                    field: `Ent${i}`,
                    headerName: 'ENT',
                    flex: 1,
                })
            if (saiExists)
                columns.push({
                    ...optionsColumn,
                    editable: true,
                    field: `Sai${i}`,
                    headerName: 'SAI',
                    flex: 1,
                })
        }
    }
    return columns as GridColDefPro[]
}
